import { useState } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { ArrowLeft, Compass } from 'lucide-react';
import { buttonClass } from '@/components/ui/Button';
import { ErrorState, PermissionDenied } from '@/components/ui/Feedback';
import { ApiError } from '@/lib/http';
import { isChunkLoadError, reloadForNewVersion } from '@/lib/staleBuild';

export function NotFoundPage() {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-16 text-center md:py-24">
      <span className="mb-2 flex size-12 items-center justify-center rounded-full bg-surface-2 text-subtle">
        <Compass className="size-6" aria-hidden />
      </span>
      <p className="font-mono text-xs text-subtle">404</p>
      <h1 className="text-lg font-semibold">Không tìm thấy trang</h1>
      <p className="max-w-md text-[13px] text-muted">Đường dẫn không tồn tại hoặc bản ghi nằm ngoài phạm vi truy cập của bạn.</p>
      <div className="mt-4 flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
        <button type="button" onClick={() => window.history.back()} className={buttonClass('secondary', 'md', 'gap-2')}>
          <ArrowLeft className="size-4" aria-hidden />
          Quay lại
        </button>
        <Link to="/" className={buttonClass('primary')}>
          Về trang tổng quan
        </Link>
      </div>
    </div>
  );
}

export function ForbiddenPage() {
  return <PermissionDenied />;
}

/** Router errorElement: catches render/loader errors so one broken page doesn't blank the app. */
export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  if (error instanceof ApiError && error.isNotFound) return <NotFoundPage />;
  if (isChunkLoadError(error)) return <NewVersionPage />;
  return (
    <div className="px-4 py-10 md:p-6">
      <ErrorState error={error} title="Trang gặp lỗi khi hiển thị" onRetry={() => window.location.reload()} />
      <p className="text-center text-xs text-subtle">
        Nếu lỗi lặp lại, gửi mã hỗ trợ (nếu có) cho bộ phận hỗ trợ. <Link to="/" className="text-accent hover:underline">Về trang chủ</Link>
      </p>
    </div>
  );
}

/** Shown when a page file of the previous version is gone after an update: reload once automatically. */
function NewVersionPage() {
  const reloading = useState(() => reloadForNewVersion())[0];
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-16 text-center md:py-24">
      <p className="text-sm font-medium text-ink">{reloading ? 'Đang tải phiên bản mới…' : 'Phần mềm vừa được cập nhật'}</p>
      {!reloading && (
        <>
          <p className="text-[13px] text-muted">Tải lại trang để dùng phiên bản mới.</p>
          <button type="button" className={buttonClass('primary')} onClick={() => window.location.reload()}>
            Tải lại trang
          </button>
        </>
      )}
    </div>
  );
}
